import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { monitorAPI } from '../services/api';

export default function Monitor() {
  const [overview, setOverview] = useState([]);
  const [alerts,   setAlerts]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [metrics,  setMetrics]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([monitorAPI.overview(), monitorAPI.alerts()])
      .then(([o, a]) => { setOverview(o.data.data); setAlerts(a.data.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    monitorAPI.metrics(selected).then(r => setMetrics(r.data.data)).catch(console.error);
  }, [selected]);

  const COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };
  const SEVERITY = { warning:'badge-yellow', info:'badge-blue', critical:'badge-red' };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div><h1>📊 Monitor</h1><p>Real-time deployment health and metrics</p></div>
        <button className="btn btn-secondary btn-sm" onClick={() => monitorAPI.overview().then(r => setOverview(r.data.data))}>↻ Refresh</button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ marginBottom:20 }}>
          {alerts.map(a => (
            <div key={a.id} style={{ display:'flex', alignItems:'center', gap:12, padding:12, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, marginBottom:8 }}>
              <span className={`badge ${SEVERITY[a.severity]}`}>{a.severity}</span>
              <span style={{ flex:1, fontSize:14 }}>{a.message}</span>
              <span style={{ fontSize:12, color:'#4d6080' }}>{new Date(a.time).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Deployment cards */}
      {overview.length === 0 ? (
        <div className="empty"><div className="empty-icon">📊</div><h3>No running deployments</h3><p>Start a deployment to see metrics here</p></div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:16, marginBottom:24 }}>
          {overview.map(d => (
            <div key={d.id} className="card" style={{ cursor:'pointer', borderTop:`3px solid ${COLORS[d.provider]}`, borderColor: selected===d.id ? 'var(--accent)' : undefined }}
              onClick={() => setSelected(selected===d.id ? null : d.id)}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontWeight:700 }}>{d.name}</div>
                  <div style={{ fontSize:12, fontFamily:'var(--font-mono)', color: COLORS[d.provider], textTransform:'uppercase' }}>{d.provider} · {d.region}</div>
                </div>
                <span className="badge badge-green">● {d.status}</span>
              </div>
              {d.publicIP && <div style={{ fontSize:12, fontFamily:'var(--font-mono)', color:'#4d6080', marginBottom:12 }}>IP: {d.publicIP}</div>}
              {/* CPU bar */}
              <div style={{ marginBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                  <span style={{ color:'#8899b4' }}>CPU</span><span>{d.health?.cpu}%</span>
                </div>
                <div style={{ height:6, background:'var(--bg-hover)', borderRadius:3 }}>
                  <div style={{ width:`${d.health?.cpu}%`, height:'100%', background:'#3b82f6', borderRadius:3, transition:'width 0.5s' }} />
                </div>
              </div>
              {/* Memory bar */}
              <div style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                  <span style={{ color:'#8899b4' }}>Memory</span><span>{d.health?.memory}%</span>
                </div>
                <div style={{ height:6, background:'var(--bg-hover)', borderRadius:3 }}>
                  <div style={{ width:`${d.health?.memory}%`, height:'100%', background:'#10b981', borderRadius:3, transition:'width 0.5s' }} />
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#4d6080' }}>
                <span>Uptime: {d.uptime}m</span>
                <span style={{ color:'#f59e0b' }}>${d.cost?.hourlyRate?.toFixed(4)}/hr</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Metrics chart */}
      {selected && metrics.length > 0 && (
        <div className="card">
          <h2 style={{ fontSize:16, marginBottom:20 }}>24-Hour Metrics — {overview.find(d=>d.id===selected)?.name}</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2d45" />
              <XAxis dataKey="time" tick={{ fill:'#8899b4', fontSize:11 }} />
              <YAxis tick={{ fill:'#8899b4', fontSize:11 }} domain={[0,100]} tickFormatter={v=>`${v}%`} />
              <Tooltip contentStyle={{ background:'#111827', border:'1px solid #1e2d45', borderRadius:8, color:'#f0f4ff' }} formatter={(v,n)=>[`${v}%`,n]} />
              <Line type="monotone" dataKey="cpu"    stroke="#3b82f6" strokeWidth={2} dot={false} name="CPU" />
              <Line type="monotone" dataKey="memory" stroke="#10b981" strokeWidth={2} dot={false} name="Memory" />
              <Line type="monotone" dataKey="network" stroke="#f59e0b" strokeWidth={2} dot={false} name="Network" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
